import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Typography,
} from "@material-tailwind/react";
import releasesData from "../data/releasesData";
import { useTranslation } from 'react-i18next';
import { useTheme } from '../DarkThemeContext';
import { PageViewTracker } from "../handlers/GoogleAnalyticsHandler";

function Releases() {
  // Track page view for Google Analytics
  PageViewTracker({ path: "/releases", title: "RELEASES" });

  // Get translation and theme information
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { theme } = useTheme();

  // State for selected version
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [documentExists, setDocumentExists] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);

  // Get all releases sorted by year (newest first) and within each year (newest first)
  const sortedReleaseData = [...releasesData].sort((a, b) => b.releaseYear - a.releaseYear);
  
  // Check screen size on component mount and when window resizes
  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024); // 1024px is the breakpoint for lg
    };
    
    // Check initially
    checkScreenSize();
    
    // Add resize listener
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup listener
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Select the most recent release by default on component mount
  useEffect(() => {
    if (sortedReleaseData.length > 0 && sortedReleaseData[0].releases.length > 0) {
      setSelectedVersion(sortedReleaseData[0].releases[0]);
    }
  }, []);

  // Handle version selection
  const handleVersionSelect = (release) => {
    if (isLargeScreen) {
      setSelectedVersion(release);
      setDocumentExists(true); // Reset document state
    } else {
      // For smaller screens, open PDF in a new tab
      window.open(release.url, '_blank');
    }
  };

  // Check if iframe loaded a PDF or a 404 page
  const handleIframeLoad = () => {
    // Clear any previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    try {
      const iframe = iframeRef.current;
      if (!iframe) return;
      
      // Create a new Image element to test loading the PDF as an image
      // This will always fail for valid PDFs but in a specific way
      const testImg = new Image();
      testImg.onload = () => {
        // If this loads as an image, it's probably an HTML page (404)
        setDocumentExists(false);
      };
      
      testImg.onerror = () => {
        // If it errors with a security error, it's likely a PDF (cross-origin)
        // Check if the iframe URL contains PDF header
        const checkPdfContent = () => {
          try {
            // Try to access iframe document title
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            const title = iframeDoc.title || '';
            
            // Most 404 pages will have titles like "404" or "Not Found"
            if (title.includes('404') || title.toLowerCase().includes('not found')) {
              setDocumentExists(false);
            } else {
              // Additional checks for PDF content
              const bodyText = iframeDoc.body ? iframeDoc.body.textContent || '' : '';
              if (bodyText.toLowerCase().includes('404') || 
                  bodyText.toLowerCase().includes('not found')) {
                setDocumentExists(false);
              }
            }
          } catch (e) {
            // If we can't access the iframe content due to CORS,
            // it's likely loading a PDF correctly
            setDocumentExists(true);
          }
        };
        
        // Give the iframe content a moment to load
        timeoutRef.current = setTimeout(checkPdfContent, 1000);
      };
      
      // Set source to the same as iframe to test
      testImg.src = selectedVersion.url;
    } catch (e) {
      console.error("Error checking PDF:", e);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 m-2 sm:m-8 h-full">
      {/* Left side - Version list */}
      <div className="w-full lg:w-1/5 overflow-y-auto dark:bg-blue-900 rounded p-4">
        <Typography variant="h4" className="text-blue-800 dark:text-yellow-800 mb-4">
          {t('releases-title')}
        </Typography>
        
        <div className="flex flex-col gap-2">
          {sortedReleaseData.map((yearData, yearIndex) => (
            <div key={yearData.releaseYear} className={yearIndex > 0 ? "mt-6" : ""}>
              <Typography variant="h6" className="text-gray-700 dark:text-gray-200 mb-2">
                {yearData.releaseYear}
              </Typography>
              
              {yearData.releases.map((release) => (
                <Button
                  key={`${yearData.releaseYear}-${release.number}`}
                  variant={isLargeScreen && selectedVersion && selectedVersion.number === release.number ? "filled" : "text"}
                  color={isLargeScreen && selectedVersion && selectedVersion.number === release.number ? "blue" : "gray"}
                  className="w-full justify-start mb-1 normal-case font-bold text-lg dark:text-white"
                  onClick={() => handleVersionSelect(release)}
                >
                  <span className="font-medium">{release.number}</span>
                </Button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Right side - PDF viewer (only for large screens) */}
      {isLargeScreen && (
        <div className="w-full lg:w-4/5 flex flex-col items-center p-4 min-h-screen">
          {selectedVersion ? (
            <>
              <Typography variant="h4" className="text-blue-800 dark:text-yellow-800 mb-4 text-center">
                {t('releases-subtitle')} <span className="ml-2">{selectedVersion.number}</span>
              </Typography>
              
              {documentExists ? (
                <div className="flex-grow w-full max-w-4xl mx-auto h-full min-h-[70vh] border border-gray-300 dark:border-gray-600 rounded">
                  <iframe 
                    ref={iframeRef}
                    src={`${selectedVersion.url}#toolbar=1&navpanes=0&scrollbar=1&view=FitH,top`}
                    className="w-full h-full min-h-[70vh]"
                    title={`Release ${selectedVersion.number}`}
                    onLoad={handleIframeLoad}
                  />
                </div>
              ) : (
                <Typography variant="h5" className="text-center text-gray-600 dark:text-gray-300 mt-8">
                  {t('releases-document-not-found')}
                </Typography>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <Typography className="text-gray-500 dark:text-gray-400 text-center">
                {t('releases-select-version')}
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Releases;